"use client";

import { motion } from "framer-motion";
import { ArrowDownLeft, ArrowUpRight, CreditCard, Gift, Plus, Wallet } from "lucide-react";

const transactions = [
  { title: "Competition reward", amount: "+ credits", time: "Example credit", type: "credit" },
  { title: "Exam entry", amount: "− credits", time: "Example debit", type: "debit" },
  { title: "Referral benefit", amount: "+ credits", time: "Example bonus", type: "bonus" },
];

export default function WalletPreview() {
  return (
    <div className="space-y-4 bg-[#07101F] p-4 text-white sm:p-5">
      <motion.div
        whileHover={{ y: -2 }}
        className="overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-br from-[#334a7d] via-[#243a69] to-[#172747] p-5 shadow-2xl sm:p-6"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-gold">TCD Wallet</p>
            <h2 className="mt-2 text-3xl font-black tracking-tight sm:text-4xl">Sample balance</h2>
            <p className="mt-1 text-sm text-white/65">Illustrative wallet preview</p>
          </div>
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-brand-gold/50 bg-white/[0.08] text-brand-gold sm:h-14 sm:w-14">
            <Wallet className="h-7 w-7" />
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.07] p-4">
          <p className="text-xs font-semibold text-white/60">Credit conversion</p>
          <p className="mt-1 text-xl font-black text-white">100 TCD Credits = ₹1</p>
          <p className="mt-2 text-[10px] leading-4 text-white/50">
            Example conversion only. Wallet balances and transaction eligibility are determined by the actual account and platform rules.
          </p>
        </div>
      </motion.div>

      <div className="grid grid-cols-3 gap-3">
        <ActionCard icon={<Plus className="h-5 w-5" />} label="Add money" />
        <ActionCard icon={<ArrowUpRight className="h-5 w-5" />} label="Withdraw" />
        <ActionCard icon={<Gift className="h-5 w-5" />} label="Rewards" />
      </div>

      <div className="overflow-hidden rounded-[28px] border border-white/10 bg-[#0b1426] p-5 shadow-xl">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-gold">Example activity</p>
            <h3 className="mt-1 text-xl font-black tracking-tight sm:text-2xl">Transactions</h3>
          </div>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-brand-gold/50 text-brand-gold">
            <CreditCard className="h-5 w-5" />
          </div>
        </div>

        <div className="mt-5 space-y-2">
          {transactions.map((item) => (
            <TransactionRow key={item.title} {...item} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ActionCard({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex min-w-0 items-center justify-center gap-2 rounded-2xl border border-white/10 bg-[#0b1426] px-2 py-4 text-center text-xs font-semibold text-white sm:text-sm">
      <span className="shrink-0 text-brand-gold">{icon}</span>
      <span>{label}</span>
    </div>
  );
}

function TransactionRow({ title, amount, time, type }: { title: string; amount: string; time: string; type: string }) {
  const positive = type === "credit" || type === "bonus";
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.025] px-3 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${
          positive ? "border-emerald-400/25 bg-emerald-400/10 text-emerald-400" : "border-red-400/25 bg-red-400/10 text-red-400"
        }`}>
          {positive ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
        </div>
        <div className="min-w-0">
          <h4 className="truncate text-xs font-semibold sm:text-sm">{title}</h4>
          <p className="mt-0.5 text-[10px] text-white/40 sm:text-xs">{time}</p>
        </div>
      </div>
      <span className={`shrink-0 text-xs font-bold sm:text-sm ${positive ? "text-emerald-400" : "text-red-400"}`}>
        {amount}
      </span>
    </div>
  );
}
